import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LocalDate } from 'src/utilities/local-date.js';
import '../../library/editors/elements/d-select-dropdown.js';
import '../../library/editors/elements/d-select-tag.js';
import '../../library/elements/d-label.js';
import '../../library/elements/d-section.js';
import '../../library/fields/d-expansion.js';
import '../../library/fields/d-view-info.js';
import type { EmployeeForStaffingCalendar } from 'src/store/selectors';
import { EmployeeViewModelGenderEnum } from '@trinnvis/dabih-api-client';
import type { SicknessAbsenceStats } from './sickness-absence-stats';
import type { QuarterData } from './quarter-data';
import type { EmployeeStatsPeriod } from './employee-stats-period';
import type { SicknessAbsenceStatsDataYear } from './sickness-absence-stats-data-year';
import type { SelectDropdownOption } from 'src/library/editors/elements/select-dropdown-option';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-sickness-absence-statistics')
export class DSicknessAbsenceStatistics extends LitElement {
  static readonly styles = [
    css`
      .genderSelector > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: 1px solid var(--borderColor);
        border-bottom: 1px solid var(--borderColor);
        margin-top: -1px;
        padding: 8px 0;
      }
      #year {
        flex: none;
      }
      .quarterHeader {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        border-top: 1px solid var(--borderColor);
        margin-top: -1px;
        padding: 16px 0 8px 0;
      }
      .quarterHeader > * {
        margin-top: 4px;
      }
      .quarterHeader h2.quarterName {
        font-size: 18px;
      }
      .quarterHeader d-label {
        margin-left: 20px;
      }
      .quarter-content {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
      }
      table {
        width: 100%;
        min-width: 372px;
        margin-top: -1px;
        margin-right: 12px;
        border-top: 1px solid var(--borderColor);
        border-collapse: collapse;
        text-align: left;
      }
      th,
      td {
        vertical-align: top;
      }
      th {
        font-weight: normal;
      }
      tr:first-child th {
        padding-top: 12px;
        padding-bottom: 6px;
      }
      tr:first-child th:first-child {
        font-size: 17px;
      }
      tr:nth-child(2) th {
        padding-bottom: 8px;
      }
      tr:first-child th:first-child {
      }
      td {
        border-top: 1px solid var(--borderColor);
        border-bottom: 1px solid var(--borderColor);
        padding: 8px 0;
      }
      th:first-child,
      td:first-child {
        padding-right: 8px;
        font-weight: bold;
      }
      tr.period td {
        font-weight: 200;
      }
      td:first-child {
        width: 220px;
      }
      td:last-child {
        width: 100px;
      }
    `,
  ];
  @property({ type: Array })
  employees: EmployeeForStaffingCalendar[] = [];
  @property({ type: String })
  selectedYear = LocalDate.now().toString().substring(0, 4);
  @property({ type: Array })
  displayOptions: string[] = [];
  @property({ type: Array })
  selectedEmployees: string[] = [];
  /**
   * Statitiskk for hver år
   */
  @property({ type: Object })
  sicknessAbsenceStats!: SicknessAbsenceStats;
  private genderOptions = [
    { value: EmployeeViewModelGenderEnum.Female, text: t('staffing.female') },
    { value: EmployeeViewModelGenderEnum.Male, text: t('staffing.male') },
  ];

  private setGender(e: CustomEvent<{ value: string[] }>, employeeUuid: string) {
    const gender =
      e.detail.value.length === 0
        ? EmployeeViewModelGenderEnum.Undefined
        : (e.detail.value[0] as EmployeeViewModelGenderEnum);
    this.dispatchEvent(
      new CustomEvent<{ uuid: string; gender: EmployeeViewModelGenderEnum }>('employee-gender', {
        bubbles: true,
        composed: true,
        detail: { uuid: employeeUuid, gender },
      }),
    );
  }

  private percentage(part: number, total: number) {
    if (part && total) {
      return Math.round((part / total) * 10000) / 100 + '%';
    }
    return '0%';
  }

  private round(n: number) {
    return Math.round(n * 100) / 100;
  }

  private roundDays(n: number) {
    const days = Math.round(n * 100) / 100;
    if (!days) {
      return t('staffing.none');
    }
    if (days === 1) {
      return t('staffing.daysSingular', { count: days });
    }
    return t('staffing.daysPlural', { count: days });
  }

  private periodsText(n: number) {
    if (!n) {
      return '';
    }
    if (n === 1) {
      return t('staffing.periodsSingular', { count: n });
    }
    return t('staffing.periodsPlural', { count: n });
  }

  private showDetails() {
    return this.displayOptions.includes('showDetails');
  }

  private years(): SelectDropdownOption[] {
    const startYear = 2018;
    const endYear = Number(LocalDate.now().toString().substring(0, 4));
    const options: any[] = [];
    for (let i = startYear; i <= endYear; i++) {
      options.push({
        value: i.toString(),
        text: i.toString(),
      });
    }
    return options;
  }

  private calculateDisplayOptions(o: string[]) {
    if (o.includes('showDetails')) {
      return [
        { value: 'showDetails', text: t('staffing.showDetails') },
        { value: 'showPeriods', text: t('staffing.showPeriods') },
      ];
    } else {
      return [{ value: 'showDetails', text: t('staffing.showDetails') }];
    }
  }

  private onSelectedYearChanged(e: CustomEvent<{ value: string }>) {
    e.stopPropagation();
    this.selectedYear = e.detail.value;
  }

  private onDisplayOptionsChanged(e: CustomEvent<{ value: string[] }>) {
    e.stopPropagation();
    this.displayOptions = e.detail.value;
  }

  private periodDays(periods: EmployeeStatsPeriod[]): string {
    // Bruk absenceDaysInQuarter for korrekt visning av dager innenfor kvartalet
    const absenceDays = periods
      .map((p) => p.absenceDaysInQuarter ?? p.absenceDays)
      .reduce((accumulator, absenceDays) => {
        return accumulator + absenceDays;
      }, 0);

    const days = Math.round(absenceDays * 100) / 100;
    if (!days) {
      return t('staffing.none');
    }
    if (days === 1) {
      return t('staffing.daysSingular', { count: days });
    }
    return t('staffing.daysPlural', { count: days });
  }

  private periodsCount(count: number): string {
    if (count === 0) {
      return '';
    }
    if (count === 1) {
      return t('staffing.periodsSingular', { count });
    }
    return t('staffing.periodsPlural', { count });
  }

  private formatDate(date: string): string {
    return LocalDate.fromString(date).toStringForDisplay();
  }

  renderPeriodRows(periods: EmployeeStatsPeriod[]) {
    const checkDurationAndNotSickChild = (
      period: EmployeeStatsPeriod,
      minInclusive: number,
      maxInclusive: number | undefined = undefined,
    ) => {
      const periodLength = LocalDate.fromString(period.start).until(LocalDate.fromString(period.end)) + 1;
      return (
        !period.types.includes('sickChildren') &&
        periodLength >= minInclusive &&
        (maxInclusive === undefined || periodLength <= maxInclusive)
      );
    };
    const periodTypes: {
      label: string;
      type: string;
      check: (p: EmployeeStatsPeriod) => boolean;
    }[] = [
      {
        label: t('staffing.upTo3Days'),
        type: 'short',
        check: (p) => checkDurationAndNotSickChild(p, 0, 3),
      },
      {
        label: t('staffing.4To16Days'),
        type: 'medium',
        check: (p) => checkDurationAndNotSickChild(p, 4, 16),
      },
      {
        label: t('staffing.over16Days'),
        type: 'long',
        check: (p) => checkDurationAndNotSickChild(p, 17),
      },
      {
        label: t('staffing.over8Weeks'),
        type: 'extraLong',
        check: (p) => checkDurationAndNotSickChild(p, 57),
      },
      {
        label: t('staffing.sickChild'),
        type: 'sickChild',
        check: (p) => p.types.includes('sickChildren'),
      },
    ];
    return periodTypes.map((type) => {
      const typePeriods = periods.filter((period) => type.check(period));
      return html`
        <tr>
          <td>${type.label}</td>
          <td>${this.periodDays(typePeriods)}</td>
          <td>${this.periodsCount(typePeriods.length)}</td>
        </tr>
        ${this.displayOptions.includes('showPeriods') && typePeriods.length > 0
          ? html` ${typePeriods.map(
              (period) =>
                html` <tr class="period">
                  <td>${period.employeeName}</td>
                  <td>${this.roundDays(period.absenceDaysInQuarter ?? period.absenceDays)}</td>
                  <td>${this.formatDate(period.start)}</td>
                </tr>`,
            )}`
          : nothing}
      `;
    });
  }

  private renderQuarterDetails(quarter: QuarterData, gender: 'female' | 'male') {
    return html`
      <table>
        <tr>
          <th>${gender === 'female' ? t('staffing.women') : t('staffing.men')}</th>
          <th colspan="2">${this.round(quarter[gender].workDays)} ${unsafeHTML(t('staffing.agreedManDaysSuffix'))}</th>
        </tr>
        <tr>
          <th>${t('staffing.sicknessAbsence')}</th>
          <th>${this.roundDays(quarter[gender].absenceDays)}</th>
          <th>${this.periodsText(quarter[gender].periodsCountTotal)}</th>
        </tr>
        ${this.renderPeriodRows(quarter[gender].periods)}
      </table>
    `;
  }

  private renderQuarter(quarter: QuarterData) {
    return html` <div class="quarterHeader">
        <h2 class="quarterName">${quarter.quarterName}</h2>
        <div>
          <d-label label="${t('staffing.quarterSicknessAbsenceExclSickChild')}"></d-label>
          ${this.percentage(quarter.absenceDays, quarter.workDays)}
        </div>
        <div>
          <d-label label="${t('staffing.totalInclCareDays')}"></d-label>
          ${this.percentage(quarter.absenceDaysTotal, quarter.workDays)}
        </div>
        <div>
          <d-label class="female" label="${t('staffing.women')}"></d-label>
          ${this.percentage(quarter.female.absenceDays, quarter.female.workDays)}
          <d-label class="male" label="${t('staffing.men')}"></d-label>
          ${this.percentage(quarter.male.absenceDays, quarter.male.workDays)}
        </div>
      </div>
      <d-expansion ?opened=${this.showDetails()}>
        <div class="quarter-content">
          ${this.renderQuarterDetails(quarter, 'female')} ${this.renderQuarterDetails(quarter, 'male')}
        </div>
      </d-expansion>`;
  }

  private renderYear(year: SicknessAbsenceStatsDataYear) {
    return html`${year.quarters.map((quarter) => this.renderQuarter(quarter))}`;
  }

  private renderMissingGenders() {
    const genderSelectorInfo = t('staffing.genderSelectorInfo');

    return html`<div class="genderSelector">
      <div class="row borderRow spaceBetween">
        <d-view-info info-list content="${genderSelectorInfo}"></d-view-info>
      </div>
      ${this.employees.map(
        (employee) =>
          html` <div>
            <div class="employeeName">${employee.name}</div>
            <d-select-tag
              @value-changed=${(e: CustomEvent) => this.setGender(e, employee.uuid)}
              .value="${employee.gender === EmployeeViewModelGenderEnum.Undefined ? [] : [employee.gender]}"
              .options=${this.genderOptions}
            ></d-select-tag>
          </div>`,
      )}
    </div>`;
  }

  render() {
    return this.sicknessAbsenceStats.genderDefined
      ? html`<d-section>
            <d-select-dropdown
              controller
              id="year"
              label="${t('staffing.year')}"
              .options="${this.years()}"
              .value="${this.selectedYear}"
              @value-changed=${this.onSelectedYearChanged}
            ></d-select-dropdown>
            <d-select-tag
              class="displayOption"
              deselectable
              label="${t('staffing.displayOptions')}"
              .options="${this.calculateDisplayOptions(this.displayOptions)}"
              type="tag"
              theme-page
              controller
              .value="${this.displayOptions}"
              @value-changed=${this.onDisplayOptionsChanged}
            ></d-select-tag>
          </d-section>
          <div class="stats">
            <div class="content">
              ${this.sicknessAbsenceStats.years
                .filter((y) => y.yearName === this.selectedYear)
                .map((year) => this.renderYear(year))}
            </div>
          </div>`
      : this.renderMissingGenders();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-sickness-absence-statistics': DSicknessAbsenceStatistics;
  }
}
